import posthog from 'posthog-js';
import { hasConsent } from './consent';

// posthog.init() is only called once analytics consent exists. Even when opted
// out, an initialized SDK fetches its remote config and lazy-loads extension
// scripts from PostHog, which sends the visitor's IP address and our project
// key before consent. Not initializing at all keeps a declining user at zero
// PostHog requests.
function startPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    return;
  }

  posthog.init(key, {
    api_host: 'https://eu.i.posthog.com',
    defaults: '2026-01-30',
    person_profiles: 'always',
    persistence: 'localStorage+cookie',
    opt_out_capturing_by_default: false,
    disable_session_recording: true,
  });
}

export function initPostHog(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!import.meta.env.VITE_POSTHOG_KEY) {
    return;
  }

  if (!hasConsent('analytics')) {
    return;
  }

  startPostHog();
}

export function enablePostHogTracking(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!posthog.__loaded) {
    startPostHog();
    if (!posthog.__loaded) {
      return;
    }
  }

  posthog.opt_in_capturing();
  posthog.set_config({ persistence: 'localStorage+cookie' });
}

export function disablePostHogTracking(): void {
  if (typeof window === 'undefined' || !posthog.__loaded) {
    return;
  }

  posthog.opt_out_capturing();
  posthog.set_config({ persistence: 'memory' });
  posthog.reset();
}

export function enableSessionReplay(): void {
  if (typeof window === 'undefined' || !posthog.__loaded) {
    return;
  }

  posthog.startSessionRecording();
}

export function disableSessionReplay(): void {
  if (typeof window === 'undefined' || !posthog.__loaded) {
    return;
  }

  posthog.stopSessionRecording();
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !posthog.__loaded) {
    return;
  }

  posthog.capture(eventName, properties);
}
